/**
 * @file dialog.tsx
 * @description Dialog-примитив поверх @base-ui/react/dialog (phase08.5).
 *
 * API совместим с типовым shadcn Dialog: Root/Trigger/Portal/Overlay/Content/
 * Header/Title/Description/Footer/Close.
 *
 * Glassmorphism overlay + soft shadow для content (Academic Luminary —
 * без 100% opaque outlines).
 */

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-[var(--z-overlay)] bg-on-surface/30 backdrop-blur-sm",
        "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        "transition-opacity duration-200",
        className,
      )}
      {...props}
    />
  );
}

interface DialogContentProps
  extends React.ComponentProps<typeof DialogPrimitive.Popup> {
  showClose?: boolean;
  /**
   * Полноэкранный диалог на узком экране: `true` — до `sm` (офисная
   * админка), `"lg"` — до `lg` (складские экраны: планшет кладовщика тоже
   * телефон по сценарию, правила warehouse-UI).
   */
  fullScreen?: boolean | "lg";
}

/**
 * Рецепты полноэкранного режима — реестром, а не тернарником в разметке:
 * граница у диалога одна из двух, и оба набора классов должны лежать рядом.
 */
const FULL_SCREEN_CLASS = {
  sm: "sm:rounded-xl sm:w-[calc(100%-2rem)] sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-lg max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:max-w-none max-sm:w-full max-sm:h-full max-sm:overflow-y-auto",
  lg: "lg:rounded-xl lg:w-[calc(100%-2rem)] lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:max-w-lg max-lg:inset-0 max-lg:translate-x-0 max-lg:translate-y-0 max-lg:rounded-none max-lg:max-w-none max-lg:w-full max-lg:h-full max-lg:overflow-y-auto",
} as const;

function DialogContent({
  className,
  children,
  showClose = true,
  fullScreen = false,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          // Слой строго выше overlay'я — иначе backdrop-blur overlay'я в
          // некоторых браузерах захватывает content в свой composite-layer и
          // модал рендерится размытым (инцидент 2026-06-05). Числа — в шкале
          // `--z-*` в `index.css`, здесь их не подбираем.
          "fixed left-1/2 top-1/2 z-[var(--z-dialog)] -translate-x-1/2 -translate-y-1/2",
          "w-[calc(100%-2rem)] max-w-lg",
          "bg-surface-lowest rounded-xl",
          "shadow-[0_20px_40px_var(--shadow-ambient)]",
          "[box-shadow:0_20px_40px_var(--shadow-ambient),inset_0_0_0_1px_var(--outline-variant)]",
          "p-6 flex flex-col gap-4",
          "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
          "data-[ending-style]:scale-95 data-[starting-style]:scale-95",
          "transition-all duration-200",
          fullScreen && FULL_SCREEN_CLASS[fullScreen === "lg" ? "lg" : "sm"],
          className,
        )}
        {...props}
      >
        {children}
        {showClose ? (
          // Нажатие 48 до 1024 и 44 с 1024 (commora-console §5), иконка 20;
          // центр — 28px от углов, как у прежней кнопки 24px.
          <DialogPrimitive.Close
            className={cn(
              "absolute right-1 top-1 inline-flex size-12 items-center justify-center rounded-md text-on-surface-variant lg:right-1.5 lg:top-1.5 lg:size-11",
              "hover:bg-surface-low hover:text-on-surface",
              "transition-colors outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <X className="size-5" />
            <span className="sr-only">Закрыть</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-1 pr-8", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2 sm:items-center",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg font-semibold tracking-tight text-on-surface",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-on-surface-variant", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
